import React, { useState, useEffect, useRef } from 'react';
import * as api from '../../api/index.js';
import useAxiosData from '../../hooks/useAxiosData.js';

import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  Cell
} from '@table-library/react-table-library/table';

import { useTheme } from '@table-library/react-table-library/theme';
import { getTheme } from '@table-library/react-table-library/baseline';
import {
  HeaderCellSort,
  SortToggleType,
  useSort
} from "@table-library/react-table-library/sort";
import { useTree, CellTree } from "@table-library/react-table-library/tree";

import {
  ButtonGroup,
  ToggleButton,
  Container,
  Row as BootstrapRow,
  Col,
  Card
} from 'react-bootstrap';

import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';



function VideoAnalyticsGraph({ data }) {
  const rowHeight = 60;
  const baseHeight = 300;
  const chartHeight = Math.max(baseHeight, data.length * rowHeight);

  return (
    <Card className="mt-4 shadow-sm">
      <Card.Body>
        
        <h2 id="video-analytics-chart" className="text-center mb-3">
          📊 Video Popularity
        </h2>
        <div className="chart-container" role="img" aria-label="video-analytics-chart">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              layout="vertical"
              data={data}
              margin={{ top: 10, right: 20, left: 20, bottom: 30 }}
              barCategoryGap={90}
              barSize={45}
              aria-label="Bar chart showing video likes, dislikes, and views"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={140} />
              <Tooltip />
              <Legend verticalAlign="top" height={36} />
              
              <Bar dataKey="likes" fill="#008055" activeBar={<Rectangle fill="green" />} />
              <Bar dataKey="dislikes" fill="#D32F2F" activeBar={<Rectangle fill="red" />} />
              <Bar dataKey="views" fill="#555555" activeBar={<Rectangle fill="grey" />} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card.Body>
    </Card>
  );
}


export default function VideoAnalytics() {
  const [sortState, setSortState] = useState({ sortKey: "NONE", reverse: false });
  const [groupBy, setGroupBy] = useState("category");

  const theme = useTheme([
    getTheme(),
    {
      Table: `--data-table-library_grid-template-columns: ${
        groupBy === "bench"
          ? "10% 15% 20% 25% 10% 10% 10%"
          : "15% 20% 35% 10% 10% 10%"
      };`
    }
  ]);

  const [videos, videoStatus] = useAxiosData(api.fetchVideos, []);
  const [benches, benchStatus] = useAxiosData(api.fetchBenches, []);
  const loading = [videoStatus, benchStatus].includes("loading");

  const tableData = groupBy === "bench"
    ? {
        nodes: benches.map(b => ({
          id: b.bench_number,
          bench: b.bench_number,
          nodes: videos
            .filter(v => b.videos.some(v2 => v2.title === v.title))
            .map(v => ({ id: v._id, child: true, bench: b.bench_number, ...v }))
        })).sort((a, b) => a.bench - b.bench)
      }
    : {
        nodes: Array.from(new Set(videos.map(v => v.category)))
          .map(category => ({
            id: category,
            category,
            nodes: videos
              .filter(v => v.category === category)
              .map(v => ({ id: v._id, child: true, ...v }))
          }))
      };

  const chartData = videos.map(v => ({
    name: v.title,
    likes: v.totLikes,
    dislikes: v.totDislikes,
    views: v.views
  }));

  const sort = useSort(
    tableData,
    { onChange: (_, s) => setSortState(s) },
    {
      sortToggleType: SortToggleType.AlternateWithReset,
      sortFns: {
        Bench: (array) => array.sort((a, b) => a.bench - b.bench),
        Title: (array) => array.flatMap(({ nodes }) => nodes).sort((a, b) => a.title.localeCompare(b.title)),
        Category: (array) =>
          groupBy === "bench"
            ? array.flatMap(({ nodes }) => nodes).sort((a, b) => a.category.localeCompare(b.category))
            : array.sort((a, b) => a.category.localeCompare(b.category)),
        Subcategory: (array) => array.map(({ nodes }) => nodes.sort((a, b) => a.subCategory.localeCompare(b.subCategory))),
        Views: (array) => array.flatMap(({ nodes }) => nodes).sort((a, b) => a.views - b.views),
        Likes: (array) => array.flatMap(({ nodes }) => nodes).sort((a, b) => a.totLikes - b.totLikes),
        Dislikes: (array) => array.flatMap(({ nodes }) => nodes).sort((a, b) => a.totDislikes - b.totDislikes),
      }
    }
  );

  const tree = useTree(tableData);

  const tableRef = useRef(null);

  useEffect(() => {
    if (loading || !tableRef.current) return;

    const addAriaLabels = () => {
      // Find any button inside the table container that has no visible text
      // and no aria-label, then add a descriptive aria-label so accessibility
      // tools (like WAVE) don't report "Empty button".
      const buttons = Array.from(tableRef.current.querySelectorAll('button'));

      buttons.forEach((btn) => {
        const hasText = (btn.textContent || '').trim().length > 0;
        const hasLabel = btn.getAttribute('aria-label');

        if (!hasText && !hasLabel) {
          // Try to infer a useful label from nearby row content
          let label = 'Toggle expand or collapse row';
          const parentRow = btn.closest('[role="row"]');
          
          if (parentRow) {
            // Get all cells in the row
            const cells = Array.from(parentRow.querySelectorAll('[role="cell"]'));
            // Find the first cell with meaningful text (skip the button cell itself)
            const textCells = cells
              .map(cell => (cell.textContent || '').trim())
              .filter(text => text && text !== '');
            
            if (textCells.length > 0) {
              // Use the first meaningful text (usually the category/bench name)
              const categoryText = textCells[0];
              label = `Toggle expand for ${categoryText} videos`;
            }
          }

          btn.setAttribute('aria-label', label);
        }
      });
    };

    // Run immediately
    addAriaLabels();

    // Also observe for dynamically added buttons
    const observer = new MutationObserver(() => {
      addAriaLabels();
    });

    observer.observe(tableRef.current, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, [loading, groupBy, tableData]);

  return (
    <Container className="container-xl bg-white p-3 shadow-sm rounded">
      
      <h1 className="text-center mb-3">🎞️ Video Analytics</h1>

      <VideoAnalyticsGraph data={chartData} />

      <BootstrapRow className="justify-content-center mt-3" as="section" aria-labelledby="group-by-label">
        <Col xs={12} md={6} className="text-center">
         
          <h2 id="group-by-label" className="h4">Group Videos By:</h2>

          <ButtonGroup role="radiogroup" aria-label="Group videos by category or bench">
            <ToggleButton
              id="group-category"
              type="radio"
              name="groupBy"
              onClick={() => setGroupBy("category")}
              variant={groupBy === "category" ? "success" : "outline-success"}
              checked={groupBy === "category"}
              aria-checked={groupBy === "category"}
              aria-label="Group by category"
            >
              Category
            </ToggleButton>

            <ToggleButton
              id="group-bench"
              type="radio"
              name="groupBy"
              onClick={() => setGroupBy("bench")}
              variant={groupBy === "bench" ? "success" : "outline-success"}
              checked={groupBy === "bench"}
              aria-checked={groupBy === "bench"}
              aria-label="Group by bench"
            >
              Bench
            </ToggleButton>
          </ButtonGroup>
        </Col>
      </BootstrapRow>

      <Card className="mt-4 shadow-sm">
        <Card.Body>
          {loading ? (
            <p className="text-center" role="status" aria-live="polite">Loading videos...</p>
          ) : (
            <>
              {/* Desktop/Tablet: Table view */}
              <div ref={tableRef} className="table-responsive d-none d-md-block" role="region" aria-label="Video analytics data table">
                <Table
                  key={groupBy}
                  data={tableData}
                  tree={tree}
                  sort={sort}
                  theme={theme}
                  layout={{ custom: true }}
                >
                  {(tableList) => (
                    <>
                      <Header>
                        <HeaderRow className="text-center bg-light">
                          {groupBy === "bench" && (
                            <HeaderCellSort sortKey="Bench">
                              <strong>Bench</strong>
                            </HeaderCellSort>
                          )}
                          <HeaderCellSort sortKey="Category"><strong>Category</strong></HeaderCellSort>
                          <HeaderCellSort sortKey="Subcategory"><strong>Subcategory</strong></HeaderCellSort>
                          <HeaderCellSort sortKey="Title"><strong>Title</strong></HeaderCellSort>
                          <HeaderCellSort sortKey="Views"><strong>Views</strong></HeaderCellSort>
                          <HeaderCellSort sortKey="Likes"><strong>Likes</strong></HeaderCellSort>
                          <HeaderCellSort sortKey="Dislikes"><strong>Dislikes</strong></HeaderCellSort>
                        </HeaderRow>
                      </Header>

                      <Body>
                        {tableList
                          .filter((item, i, self) => self.findIndex(a => a.id === item.id) === i)
                          .map(item => (
                            <Row key={item.id} item={item} className="min-w-xl">
                              {groupBy === "bench" ? (
                                <>
                                  {["NONE", "Bench"].includes(sortState.sortKey) ? (
                                    <CellTree item={item}>
                                      {!item.child && `#${item.bench}`}
                                    </CellTree>
                                  ) : (
                                    <Cell item={item}>#{item.bench}</Cell>
                                  )}
                                  <Cell item={item}>{item.category}</Cell>
                                </>
                              ) : (
                                <CellTree item={item}>
                                  {(!item.child || !["NONE", "Bench", "Category"].includes(sortState.sortKey)) &&
                                    item.category}
                                </CellTree>
                              )}
                              <Cell>{item.subCategory}</Cell>
                              <Cell>
                                <a href={item.url} aria-label={`Open video titled ${item.title}`}>
                                  {item.title}
                                </a>
                              </Cell>
                              <Cell>{item.views}</Cell>
                              <Cell>{item.totLikes}</Cell>
                              <Cell>{item.totDislikes}</Cell>
                            </Row>
                          ))}
                      </Body>
                    </>
                  )}
                </Table>
              </div>

              {/* Mobile: Card view */}
              <div className="d-md-none">
                {tableData.nodes.flatMap(group => 
                  group.nodes?.length > 0 ? group.nodes : []
                ).length > 0 ? (
                  tableData.nodes.map(group => (
                    <div key={group.id}>
                      {/* Group Header */}
                      {group.nodes?.length > 0 && (
                        <>
                          <h3 className="h5 mt-3 mb-3 fw-bold text-primary">
                            {groupBy === "bench" ? `Bench #${group.bench}` : group.category}
                          </h3>
                          
                          {/* Video Cards */}
                          {group.nodes.map(video => (
                            <div key={video.id} className="card mb-3 shadow-sm">
                              <div className="card-body">
                                <h4 className="card-title mb-2 h5">
                                  <a href={video.url} aria-label={`Open video titled ${video.title}`}>
                                    {video.title}
                                  </a>
                                </h4>
                                
                                <div className="mb-3">
                                  <span className="badge bg-secondary me-2">{video.category}</span>
                                  <span className="text-muted small">{video.subCategory}</span>
                                </div>
                                
                                <div className="row g-3 pb-2">
                                  <div className="col-4 text-center">
                                    <div className="text-muted small">Views</div>
                                    <div className="fw-bold">{video.views}</div>
                                  </div>
                                  <div className="col-4 text-center">
                                    <div className="text-muted small">Likes</div>
                                    <div className="fw-bold text-success">{video.totLikes}</div>
                                  </div>
                                  <div className="col-4 text-center">
                                    <div className="text-muted small">Dislikes</div>
                                    <div className="fw-bold text-danger">{video.totDislikes}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center p-4 text-muted">
                    No video data available
                  </div>
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

    </Container>
  );
}
